import type {
  SpaceListResponse,
  SpaceResponse,
  CreateSpaceRequest,
  UpdateSpaceRequest,
  ObjectListResponse,
  ObjectResponse,
  ObjectDetailResponse,
  CreateObjectRequest,
  UpdateObjectRequest,
  SearchResponse,
  SearchParams,
  ListObjectsParams,
} from '@arkitekt/shared';
import { useAuthStore } from '@/stores/auth.store';

const API_URL = import.meta.env.VITE_API_URL || '';
const API_BASE = `${API_URL}/api/v1`;

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().logout();
    }
    const error = await response.json().catch(() => ({}));
    throw new ApiError(response.status, error.detail || 'Request failed', error);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// Auth
export const authApi = {
  login: (code: string, redirectUri: string) =>
    request<{ accessToken: string; refreshToken: string; user: unknown }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ code, redirectUri }),
    }),

  logout: () =>
    request<void>('/auth/logout', {
      method: 'POST',
    }),

  me: () => request<{ id: string; email: string; displayName: string }>('/auth/me'),
};

// Spaces
export const spacesApi = {
  list: (params?: { page?: number; limit?: number; includePublic?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.includePublic !== undefined) searchParams.set('includePublic', String(params.includePublic));
    const query = searchParams.toString();
    return request<SpaceListResponse>(`/spaces${query ? `?${query}` : ''}`);
  },

  get: (spaceId: string) => request<SpaceResponse>(`/spaces/${spaceId}`),

  getByKey: (key: string) => request<SpaceResponse>(`/spaces/key/${key}`),

  create: (data: CreateSpaceRequest) =>
    request<SpaceResponse>('/spaces', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (spaceId: string, data: UpdateSpaceRequest) =>
    request<SpaceResponse>(`/spaces/${spaceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (spaceId: string) =>
    request<void>(`/spaces/${spaceId}`, {
      method: 'DELETE',
    }),
};

// Objects
export const objectsApi = {
  list: (spaceId: string, params?: ListObjectsParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.type) searchParams.set('type', params.type);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.parentId) searchParams.set('parentId', params.parentId);
    if (params?.sort) searchParams.set('sort', params.sort);
    if (params?.order) searchParams.set('order', params.order);
    if (params?.tags?.length) params.tags.forEach((t) => searchParams.append('tags', t));
    const query = searchParams.toString();
    return request<ObjectListResponse>(`/spaces/${spaceId}/objects${query ? `?${query}` : ''}`);
  },

  get: (spaceId: string, objectId: string) =>
    request<ObjectDetailResponse>(`/spaces/${spaceId}/objects/${objectId}`),

  create: (spaceId: string, data: CreateObjectRequest) =>
    request<ObjectResponse>(`/spaces/${spaceId}/objects`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (spaceId: string, objectId: string, data: UpdateObjectRequest) =>
    request<ObjectResponse>(`/spaces/${spaceId}/objects/${objectId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (spaceId: string, objectId: string) =>
    request<void>(`/spaces/${spaceId}/objects/${objectId}`, {
      method: 'DELETE',
    }),

  bulkImport: (spaceId: string, data: { items: Array<{ type: string; name: string; description?: string; status?: string; attributes?: Record<string, unknown> }>; skipDuplicates?: boolean; dryRun?: boolean }) =>
    request<{ created: number; skipped: number; errors: Array<{ index: number; name: string; error: string }>; dryRun: boolean }>(`/spaces/${spaceId}/objects/bulk`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Relations
export interface RelationResponse {
  id: string;
  type: string;
  sourceId: string;
  targetId: string;
  source: { id: string; name: string; type: string };
  target: { id: string; name: string; type: string };
  attributes: Record<string, unknown>;
  createdBy: string;
  createdAt: string;
}

export const relationsApi = {
  list: (spaceId: string, objectId: string, direction: 'incoming' | 'outgoing' | 'both' = 'both') =>
    request<{ data: RelationResponse[] }>(
      `/spaces/${spaceId}/objects/${objectId}/relations?direction=${direction}`
    ),
};

// Search
export const searchApi = {
  search: (params: SearchParams) => {
    const searchParams = new URLSearchParams();
    searchParams.set('q', params.q);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.types?.length) params.types.forEach((t) => searchParams.append('types', t));
    if (params.spaces?.length) params.spaces.forEach((s) => searchParams.append('spaces', s));
    if (params.tags?.length) params.tags.forEach((t) => searchParams.append('tags', t));
    if (params.status?.length) params.status.forEach((s) => searchParams.append('status', s));
    return request<SearchResponse>(`/search?${searchParams.toString()}`);
  },
};

// Graph / Impact Analysis
export interface ImpactNode {
  id: string;
  name: string;
  type: string;
  distance: number;
  impactPath: string[];
  relationTypes: string[];
}

export interface ImpactAnalysisResponse {
  sourceId: string;
  sourceName: string;
  sourceType: string;
  direction: 'incoming' | 'outgoing' | 'both';
  depth: number;
  nodes: ImpactNode[];
  summary: {
    totalAffected: number;
    byType: Record<string, number>;
    byDistance: Record<string, number>;
  };
}

export const graphApi = {
  traverse: (startId: string, depth = 3, direction: 'incoming' | 'outgoing' | 'both' = 'both') =>
    request<{ nodes: Array<{ id: string; object: Record<string, unknown>; depth: number }>; edges: Array<{ id: string; source: string; target: string; relationType: string }> }>(
      `/graph/traverse?startId=${startId}&depth=${depth}&direction=${direction}`
    ),

  impact: (objectId: string, depth = 3, direction: 'incoming' | 'outgoing' | 'both' = 'outgoing') =>
    request<ImpactAnalysisResponse>(
      `/graph/${objectId}/impact?depth=${depth}&direction=${direction}`
    ),
};

export { ApiError };
